// Shared UI components for Wallet
import { motion } from "framer-motion";
import { SBER, tapAnimation } from "../utils/constants";
import { 
  Plus, ArrowUpRight, ArrowLeftRight, Clock, Check, X, RefreshCw, Home, User 
} from "lucide-react";

// USDT Icon Component
export const USDTIcon = ({ size = "md" }) => {
  const sizes = { sm: "w-8 h-8 text-sm", md: "w-11 h-11 text-base", lg: "w-14 h-14 text-xl" };
  return (
    <div className={`${sizes[size]} rounded-xl flex items-center justify-center`} style={{ background: SBER.green }}>
      <span className="text-white font-bold">₮</span>
    </div>
  );
};

// Action Button Component
export const SberButton = ({ icon: Icon, label, primary, onClick }) => (
  <motion.button
    whileTap={tapAnimation}
    onClick={onClick}
    data-testid={`action-${label?.toLowerCase().replace(/\s/g, '-')}`}
    className="flex flex-col items-center gap-2"
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-200"
      style={{ 
        background: primary ? SBER.green : SBER.white,
        color: primary ? SBER.white : SBER.green,
        boxShadow: primary ? '0 4px 16px rgba(33, 160, 56, 0.3)' : '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      <Icon className="w-6 h-6" strokeWidth={2} />
    </div>
    <span className="text-[11px] font-medium" style={{ color: primary ? SBER.green : SBER.gray }}>
      {label}
    </span>
  </motion.button>
);

// Card Component
export const SberCard = ({ children, className = "", onClick, accent, ...props }) => (
  <motion.div
    whileTap={onClick ? tapAnimation : undefined}
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl transition-all duration-200 ${className} ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''}`}
    style={{ 
      background: accent ? SBER.green : SBER.white,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
    }}
    {...props}
  >
    {children}
  </motion.div>
);

// Transaction Item Component
export const TransactionItem = ({ tx, t }) => {
  const typeConfig = {
    TOPUP_USD: { labelKey: "history.transactionTypes.topup", icon: Plus, positive: true },
    TOPUP_KGS: { labelKey: "history.transactionTypes.topup", icon: Plus, positive: true },
    PAYOUT_USD_TO_CARD: { labelKey: "history.transactionTypes.withdraw", icon: ArrowUpRight, positive: false },
    PAYOUT_KGS_TO_CARD: { labelKey: "history.transactionTypes.withdraw", icon: ArrowUpRight, positive: false },
    EXCHANGE_KGS_TO_USD: { labelKey: "history.transactionTypes.exchange", icon: ArrowLeftRight, positive: true },
    EXCHANGE_USD_TO_KGS: { labelKey: "history.transactionTypes.exchange", icon: ArrowLeftRight, positive: false },
    EXCHANGE: { labelKey: "history.transactionTypes.exchange", icon: ArrowLeftRight, positive: true }
  };
  
  const statusConfig = {
    COMPLETED: { icon: Check, color: SBER.green },
    PENDING_PAYMENT: { icon: Clock, color: '#F59E0B' },
    PROCESSING: { icon: RefreshCw, color: SBER.green, spin: true },
    PROCESSING_PAYOUT: { icon: RefreshCw, color: SBER.green, spin: true },
    FAILED: { icon: X, color: '#EF4444' },
    CANCELED: { icon: X, color: SBER.grayLight }
  };
  
  const isTopup = (tx.type || '').startsWith('TOPUP');
  const type = typeConfig[tx.type] || (isTopup ? typeConfig.TOPUP_USD : typeConfig.PAYOUT_USD_TO_CARD);
  const status = statusConfig[tx.status] || statusConfig.PENDING_PAYMENT;
  const StatusIcon = status.icon;
  const TypeIcon = type.icon;
  const amountText = tx.amount != null && tx.currency
    ? `${Number(tx.amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${tx.currency}`
    : tx.type === 'EXCHANGE' && tx.from_amount != null
      ? `${Number(tx.from_amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${tx.from_currency} → ${Number(tx.to_amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${tx.to_currency}`
      : `${(tx.amount_usd ?? tx.amount_usdt ?? 0).toFixed(2)} $`;
  const subText = tx.amount != null && tx.currency ? (tx.method || '') : (tx.amount_kgs ? `${tx.amount_kgs.toFixed(0)} KGS` : '');
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-4 bg-white rounded-xl"
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: SBER.greenPale }}
        >
          <TypeIcon className="w-5 h-5" style={{ color: SBER.green }} strokeWidth={2} />
        </div>
        <div>
          <p className="font-medium text-[15px]" style={{ color: SBER.dark }}>{t(type.labelKey)}</p>
          <p className="text-[13px]" style={{ color: SBER.grayLight }}>
            {new Date(tx.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right">
          <p className="font-semibold text-[15px]" style={{ color: type.positive ? SBER.green : '#EF4444' }} data-testid="tx-amount">
            {type.positive ? '+' : '−'}{amountText}
          </p>
          <p className="text-[13px]" style={{ color: SBER.grayLight }}>{subText}</p>
        </div>
        <StatusIcon 
          className={`w-4 h-4 ${status.spin ? 'animate-spin' : ''}`} 
          style={{ color: status.color }} 
        />
      </div>
    </motion.div>
  );
};

// Menu Item Component (for profile page)
export const MenuItem = ({ icon: Icon, label, onClick, color, badge, external, ...props }) => (
  <motion.button
    whileTap={tapAnimation}
    onClick={onClick}
    className="w-full flex items-center justify-between p-4 transition-colors"
    style={{ background: 'transparent' }}
    {...props}
  >
    <div className="flex items-center gap-3">
      <div 
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: color ? `${color}15` : SBER.grayPale }}
      >
        <Icon className="w-5 h-5" style={{ color: color || SBER.gray }} strokeWidth={2} />
      </div>
      <span className="text-[15px]" style={{ color: SBER.dark }}>{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {badge && (
        <span 
          className="px-2 py-0.5 rounded-full text-[11px] font-medium"
          style={{ background: SBER.greenPale, color: SBER.green }}
        >
          {badge}
        </span>
      )}
      {external ? (
        <ArrowUpRight className="w-4 h-4" style={{ color: SBER.grayLight }} />
      ) : (
        <div className="w-4 h-4 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: SBER.grayLight }} />
        </div>
      )}
    </div>
  </motion.button>
);


// Bottom Navigation Component
export const BottomNav = ({ active, onNavigate, t }) => {
  const items = [
    { id: 'home', icon: Home, labelKey: 'nav.home' },
    { id: 'history', icon: Clock, labelKey: 'nav.history' },
    { id: 'profile', icon: User, labelKey: 'nav.profile' }
  ];
  
  return (
    <div className="fixed bottom-5 left-5 right-5 max-w-[398px] mx-auto">
      <div 
        className="rounded-2xl flex items-center justify-around py-2 px-2"
        style={{ 
          background: SBER.white,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        {items.map((item) => (
          <motion.button
            key={item.id}
            whileTap={tapAnimation}
            onClick={() => onNavigate(item.id)}
            data-testid={`nav-${item.id}`}
            className="flex flex-col items-center gap-1 px-7 py-2.5 rounded-xl transition-all"
            style={{ 
              background: active === item.id ? SBER.greenPale : 'transparent',
              color: active === item.id ? SBER.green : SBER.grayLight
            }}
          >
            <item.icon className="w-5 h-5" strokeWidth={2} />
            <span className="text-[11px] font-medium">{t(item.labelKey)}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};
